import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import { Button, ContentLoader, List, StatusCard } from 'shared/components';
import { displayValue, formatDate } from 'shared/helpers';
import { fetchLicenseUserGroups } from '../../licenses/actions';

const LicenseUserGroups = ({ licenseUserId }) => {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    if (!licenseUserId) return;
    setLoading(true);
    setError(false);
    fetchLicenseUserGroups(licenseUserId)
      .then((res) => {
        const payload = get(res, 'data');
        const results = Array.isArray(payload)
          ? payload
          : get(payload, 'results', []);
        setGroups(results);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setGroups([]);
        setLoading(false);
      });
  }, [licenseUserId, retryToken]);

  const handleRetry = () => setRetryToken((token) => token + 1);

  const columns = [
    {
      Header: t('Created on'),
      accessor: 'created_at',
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 140,
    },
    {
      Header: t('Group Name'),
      accessor: 'name',
      Cell: (cellData) => displayValue(cellData.value),
      width: 250,
    },
    {
      Header: t('Description'),
      accessor: 'description',
      Cell: (cellData) => displayValue(cellData.value),
    },
    {
      Header: t('Customer Account'),
      id: 'customer_account',
      accessor: (row) => get(row, 'customer_account'),
      Cell: (cellData) => displayValue(cellData.value),
      width: 200,
    },
    {
      Header: t('Number of Users'),
      id: 'number_of_users',
      className: 'text-center',
      headerClassName: 'text-center',
      accessor: (row) => get(row, 'license_users', []).length,
      Cell: (cellData) => displayValue(cellData.value, 0),
      width: 140,
    },
    {
      Header: t('Status'),
      id: 'status',
      headerClassName: 'text-center',
      className: 'text-center',
      Cell: () => <StatusCard status="success" text={t('Active')} />,
      width: 110,
    },
  ];

  if (loading) {
    return <ContentLoader text={t('Loading user groups')} />;
  }

  if (error) {
    return (
      <div className="LicenseUserDetails-error">
        <p>{t('Unable to load user groups for this user')}</p>
        <Button theme="info" size="sm" onClick={handleRetry}>
          {t('Retry')}
        </Button>
      </div>
    );
  }

  if (!groups.length) {
    return (
      <div className="LicenseUserDetails-empty">
        {t('No user groups for this user')}
      </div>
    );
  }

  return (
    <div className="LicenseUserDetails-list">
      <List
        data={groups}
        columns={columns}
        minRows={groups.length || 5}
        showPagination
        loading={loading}
        defaultPageSize={20}
      />
    </div>
  );
};

LicenseUserGroups.propTypes = {
  licenseUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default LicenseUserGroups;
